import React, { useMemo, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  type Edge,
  type Node,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';

import { buildActivityEdges, buildSemanticEdges, listFolderTree, type GraphEdgeInfo, type GraphNodeInfo } from '@/lib/graph/dataSources';
import { isRuVectorAvailable } from '@/lib/ruvectorClient';
import { addAllowlistPrefix, getAllowlistPrefixes, removeAllowlistPrefix, setAllowlistPrefixes } from '@/lib/agentAccess';

import { FileText, Folder, MessagesSquare, Briefcase, Link2, Shield } from 'lucide-react';

export type GraphScope = 'documents' | 'desktop' | 'projects' | 'chats';

export type FileGraphProps = {
  scope: GraphScope;
  showContainment: boolean;
  showSemantic: boolean;
  showActivity: boolean;
  semanticThreshold: number;
  topN: number;
  buildSignal: number;
  clearSignal: number;
  onAllowlistApplied?: (prefixes: string[]) => void;
};

type BuildState = 'idle' | 'building' | 'ready' | 'error';

type GraphBuild = {
  nodes: GraphNodeInfo[];
  edges: GraphEdgeInfo[];
};

function badgeForType(t: GraphNodeInfo['type']) {
  switch (t) {
    case 'folder':
      return { label: 'Folder', variant: 'secondary' as const };
    case 'file':
      return { label: 'File', variant: 'outline' as const };
    case 'chat':
      return { label: 'Chat', variant: 'default' as const };
    case 'project':
      return { label: 'Project', variant: 'default' as const };
    default:
      return { label: t, variant: 'outline' as const };
  }
}

function iconForType(t: GraphNodeInfo['type']) {
  switch (t) {
    case 'folder':
      return Folder;
    case 'file':
      return FileText;
    case 'chat':
      return MessagesSquare;
    case 'project':
      return Briefcase;
    default:
      return FileText;
  }
}

function colorForType(t: GraphNodeInfo['type']) {
  switch (t) {
    case 'folder':
      return '#94a3b8'; // slate
    case 'file':
      return '#22c55e'; // green
    case 'chat':
      return '#3b82f6'; // blue
    case 'project':
      return '#f59e0b'; // amber
    default:
      return '#94a3b8';
  }
}

function edgeStyle(type: GraphEdgeInfo['type']) {
  switch (type) {
    case 'containment':
      return { stroke: '#9ca3af', strokeWidth: 1 };
    case 'semantic':
      return { stroke: '#a855f7', strokeWidth: 2 };
    case 'activity':
      return { stroke: '#3b82f6', strokeWidth: 2 };
    default:
      return { stroke: '#9ca3af', strokeWidth: 1 };
  }
}

function resolveBasePaths(): { documents?: string; desktop?: string } {
  // We store synced VFS roots under Windows-style paths.
  // We reconstruct from the existing user-directory convention and let missing roots no-op.
  return {
    documents: 'C:\\User\\User\\Documents',
    desktop: 'C:\\User\\User\\Desktop',
  };
}

export default function FileGraph(props: FileGraphProps) {
  const { scope, showContainment, showSemantic, showActivity, semanticThreshold, topN, buildSignal, clearSignal, onAllowlistApplied } = props;

  const [state, setState] = useState<BuildState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [build, setBuild] = useState<GraphBuild>({ nodes: [], edges: [] });
  const [selected, setSelected] = useState<GraphNodeInfo | null>(null);

  const [selectedAllow, setSelectedAllow] = useState<Record<string, boolean>>({});

  const semanticEnabled = isRuVectorAvailable();

  const docsPath = useMemo(() => {
    // Use the synced VFS roots and fall back to the default placeholder paths when needed.
    const { documents, desktop } = resolveBasePaths();

    switch (scope) {
      case 'desktop':
        return desktop || 'C:\\User\\User\\Desktop';
      case 'projects':
        return (documents || 'C:\\User\\User\\Documents') + '\\Projects';
      case 'chats':
        return (documents || 'C:\\User\\User\\Documents') + '\\Chats';
      case 'documents':
      default:
        return documents || 'C:\\User\\User\\Documents';
    }
  }, [scope]);

  React.useEffect(() => {
    if (!buildSignal) return;

    let cancelled = false;

    const run = async () => {
      setState('building');
      setError(null);
      setSelected(null);

      try {
        const tree = await listFolderTree(docsPath);

        const activity = showActivity
          ? await buildActivityEdges((resolveBasePaths().documents || 'C:\\User\\User\\Documents'))
          : { nodes: [], edges: [] };

        const semantic = showSemantic && semanticEnabled
          ? await buildSemanticEdges(tree.filesForSemantic, topN, semanticThreshold)
          : [];

        const nodes = [...tree.nodes];
        const edges: GraphEdgeInfo[] = [];

        if (showContainment) edges.push(...tree.containmentEdges);
        if (showActivity) {
          // add any activity nodes not already present
          const existing = new Set(nodes.map((n) => n.id));
          for (const n of activity.nodes) {
            if (!existing.has(n.id)) nodes.push(n);
          }
          edges.push(...activity.edges);
        }
        if (showSemantic && semanticEnabled) edges.push(...semantic);

        if (cancelled) return;
        setBuild({ nodes, edges });
        setState('ready');
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to build graph');
        setState('error');
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [buildSignal, docsPath, showContainment, showActivity, showSemantic, semanticEnabled, semanticThreshold, topN]);

  React.useEffect(() => {
    if (!clearSignal) return;
    setBuild({ nodes: [], edges: [] });
    setSelected(null);
    setSelectedAllow({});
    setState('idle');
    setError(null);
  }, [clearSignal]);

  const rfNodes: Node[] = useMemo(() => {
    return build.nodes.map((n, idx) => {
      const Icon = iconForType(n.type);
      return {
        id: n.id,
        data: {
          label: (
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4" />
              <span className="truncate max-w-[180px]">{n.name}</span>
            </div>
          ),
        },
        position: { x: (idx % 10) * 220, y: Math.floor(idx / 10) * 90 },
        style: {
          border: '1px solid rgba(0,0,0,0.12)',
          borderRadius: 10,
          padding: 10,
          background: 'white',
          width: 220,
        },
        className: 'shadow-sm',
      };
    });
  }, [build.nodes]);

  const rfEdges: Edge[] = useMemo(() => {
    return build.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      label: e.type === 'semantic' ? e.label : undefined,
      animated: e.type !== 'containment',
      style: edgeStyle(e.type),
    }));
  }, [build.edges]);

  const allowlist = useMemo(() => getAllowlistPrefixes(), [buildSignal, clearSignal, state]);

  const toggleSelectedAllow = (node: GraphNodeInfo) => {
    setSelectedAllow((prev) => ({
      ...prev,
      [node.path]: !prev[node.path],
    }));
  };

  const applySelectionAsAllowlist = () => {
    const picked = Object.entries(selectedAllow)
      .filter(([, v]) => v)
      .map(([p]) => p);
    setAllowlistPrefixes(picked);
    onAllowlistApplied?.(picked);
  };

  const onNodeClick = (_: any, node: Node) => {
    const info = build.nodes.find((n) => n.id === node.id) || null;
    setSelected(info);
  };

  const inAllowlist = selected?.path ? allowlist.some((p) => selected.path === p || selected.path.startsWith(p + '\\')) : false;

  return (
    <div className="h-full w-full grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4">
      <div className="rounded-lg border overflow-hidden">
        {!semanticEnabled && showSemantic && (
          <div className="border-b bg-muted/40 p-3 text-sm flex items-center justify-between">
            <div className="text-muted-foreground">
              Semantic edges are unavailable in the web-only CanXP AI build.
            </div>
            <Badge variant="secondary">Unavailable on web</Badge>
          </div>
        )}

        <div className="h-[70vh] min-h-[520px] bg-gradient-to-br from-background to-muted/40">
          <ReactFlowProvider>
            <ReactFlow
              nodes={rfNodes}
              edges={rfEdges}
              onNodeClick={onNodeClick}
              fitView
              proOptions={{ hideAttribution: true }}
            >
              <MiniMap nodeColor={(n) => {
                const info = build.nodes.find((x) => x.id === n.id);
                return info ? colorForType(info.type) : '#94a3b8';
              }} />
              <Controls />
              <Background />
            </ReactFlow>
          </ReactFlowProvider>
        </div>

        <div className="border-t p-3 text-xs text-muted-foreground flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2"><Link2 className="h-3 w-3" />Containment (gray)</div>
          <div className="flex items-center gap-2"><Link2 className="h-3 w-3 text-[#a855f7]" />Semantic (purple)</div>
          <div className="flex items-center gap-2"><Link2 className="h-3 w-3 text-blue-500" />Activity (blue)</div>
          <div className="ml-auto">Nodes: {build.nodes.length} • Edges: {build.edges.length}</div>
        </div>
      </div>

      <Card className="h-fit">
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-semibold">Inspector</div>
              <div className="text-xs text-muted-foreground">Select a node to view details and manage allowlist.</div>
            </div>
            <Badge variant={state === 'ready' ? 'default' : 'secondary'}>{state}</Badge>
          </div>

          {error && <div className="mt-3 text-sm text-destructive">{error}</div>}

          <Separator className="my-4" />

          {!selected ? (
            <div className="text-sm text-muted-foreground">No node selected.</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {React.createElement(iconForType(selected.type), { className: 'h-4 w-4' })}
                <div className="font-medium truncate">{selected.name}</div>
                <Badge variant={badgeForType(selected.type).variant}>{badgeForType(selected.type).label}</Badge>
              </div>
              <div className="text-xs break-all text-muted-foreground">{selected.path}</div>
              <div className="text-xs text-muted-foreground">
                {typeof selected.size === 'number' ? `Size: ${selected.size} bytes` : 'Size: —'}
                {' • '}
                {typeof selected.modifiedMs === 'number' ? `Modified: ${new Date(selected.modifiedMs).toLocaleString()}` : 'Modified: —'}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={inAllowlist ? 'secondary' : 'default'}
                  onClick={() => {
                    if (!selected?.path) return;
                    if (inAllowlist) removeAllowlistPrefix(selected.path);
                    else addAllowlistPrefix(selected.path);
                  }}
                >
                  <Shield className="h-4 w-4 mr-2" />
                  {inAllowlist ? 'Remove from allowlist' : 'Add to allowlist'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleSelectedAllow(selected)}
                >
                  {selectedAllow[selected.path] ? 'Unselect for allowlist' : 'Select for allowlist'}
                </Button>
              </div>
            </div>
          )}

          <Separator className="my-4" />

          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-medium">Allowlist (preview)</div>
            <Button size="sm" onClick={applySelectionAsAllowlist} disabled={Object.values(selectedAllow).every((v) => !v)}>
              Apply selection
            </Button>
          </div>

          <div className="mt-2 text-xs text-muted-foreground">
            Selected prefixes will be used by agent-marked file operations.
          </div>

          <ScrollArea className="mt-3 h-[220px] rounded-md border">
            <div className="p-2 space-y-2">
              {allowlist.length === 0 ? (
                <div className="text-sm text-muted-foreground">No allowlist set.</div>
              ) : (
                allowlist.map((p) => (
                  <div key={p} className="flex items-center justify-between gap-2">
                    <div className="text-xs break-all">{p}</div>
                    <Button size="sm" variant="ghost" onClick={() => removeAllowlistPrefix(p)}>
                      Remove
                    </Button>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </Card>
    </div>
  );
}